












































































































































































import { Component, Watch } from "vue-property-decorator";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { 
  ProjectManagementSettings, 
  ProjectEditingSettings,
  ProjectNameTemplateField,
  DEFAULT_PROJECT_NAME_TEMPLATE,
  DisplaySettings,
  DeviceAssignmentSettings,
} from "@/data/models/Settings";
import { ProjectManagementSettingsModule } from "@/store/modules/ProjectManagementSettingsModule";
import { defineOwnProjectManagementSettingsClientAccess } from "@/permissions/ClientPermissions";
import { createDefaultProjectEditingSettings } from "@/data/models/Client";
import { updateProjectManagementSettings } from "@/data/repository/SettingsRepository";
import { compareArrays, filterOutUndefined } from "@/utils/Utils";
import PickerDialogComponent from "@/components/common/PickerDialog.vue";
import { loadClient } from "@/data/repository/ClientRepository";
import { toNumber } from "lodash";
import { ExtendedProjectType, formatProjectScope, formatProjectTypeName, getDefaultProjectTypes, getProjectTypeCode, ProjectTypeConfig } from "@/data/models/ProjectDetails";
import { projectSettingsAccess } from "@/permissions/UserPermissions";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import SettingsPageMixin from "@/components/settings/SettingsPageMixin";
import TemplateFieldEditorComponent from "@/components/common/TemplateFieldEditor.vue";
import { renderTemplateSample } from "@/utils/TemplateFieldOrdering";
import { getEffectiveUser } from "@/utils/ViewAs";
import { isProjectTypeShown, projectTypesForSave } from "@/utils/ProjectTypeAllowList";

@Component({
  components: {
    "confirmation-dialog": ConfirmationDialogComponent,
    "template-field-editor": TemplateFieldEditorComponent,
    picker: PickerDialogComponent,
  },
})
export default class ProjectSettingsComponent extends SettingsPageMixin {
  isInitializing = true;

  defaultDryingDurationInDays = 14;
  defaultFollowUpInspectionIntervalInDays = 0;

  projectOverviewTilesForOfficeProjects = false;
  initialProjectOverviewTilesForOfficeProjects = false;

  roomLevelDeviceAssignment = false;
  initialRoomLevelDeviceAssignment = false;

  projectTypes: ProjectTypeConfig[] = [];
  initialProjectTypes: ProjectTypeConfig[] = [];
  /** The project types IRES gives the client (#2610081); null for every type of its products. */
  allowedProjectTypeIds: number[] | null = null;

  activeTemplateFields: ProjectNameTemplateField[] = [...DEFAULT_PROJECT_NAME_TEMPLATE];
  initialTemplateFields: ProjectNameTemplateField[] = [...DEFAULT_PROJECT_NAME_TEMPLATE];

  commaTemplateField = ProjectNameTemplateField.COMMA;

  initialProjectEditingSettings: ProjectEditingSettings = createDefaultProjectEditingSettings();

  yesNoValues = [true, false];

  @Watch("projectManagementSettings")
  onProjectManagementSettingsChanged() {
    const projectEditingSettings = this.projectManagementSettings.projectEditingSettings;
    // Re-read the page from the stored document; the editing settings only when stored. (Projektorganisation
    // used to trigger this for most clients; it is set in the IRES Admin Client since 2026-10-29.)
    this.initializeParameters(
      projectEditingSettings && !projectEditingSettings.areDefaultProjectEditingSettings ? projectEditingSettings : undefined
    );
  }

  get hasChanged() {
    return (
      this.defaultDryingDurationInDays !== this.initialProjectEditingSettings.defaultDryingDurationInDays ||
      this.defaultFollowUpInspectionIntervalInDays !== this.initialProjectEditingSettings.defaultFollowUpInspectionIntervalInDays ||
      this.projectOverviewTilesForOfficeProjects !== this.initialProjectOverviewTilesForOfficeProjects ||
      this.roomLevelDeviceAssignment !== this.initialRoomLevelDeviceAssignment ||
      !this.equalProjectTypes(this.projectTypes, this.initialProjectTypes) ||
      !compareArrays(this.activeTemplateFields, this.initialTemplateFields)
    );
  }

  get projectManagementSettings() {
    return ProjectManagementSettingsModule.projectManagementSettings;
  }

  /** The user whose view this shows: the chosen one while "Sicht auf" is active (ticket 2610052). */
  get activeUser() {
    return getEffectiveUser();
  }

  get isALlowedToConfigureProjectTypes() {
    return projectSettingsAccess(this.activeUser)
  }

  get projectEditingSettings() {
    return this.projectManagementSettings.projectEditingSettings;
  }

  get canEdit() {
    return defineOwnProjectManagementSettingsClientAccess();
  }

  get readonly() {
    return !this.isEditMode || !this.canEdit;
  }

  // Compare two arrays of ProjectTypeConfig (order-sensitive for simplicity)
  private equalProjectTypes(a: ProjectTypeConfig[], b: ProjectTypeConfig[]) {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  /** The rows the table shows: the types IRES gives the client (#2610081); the others stay stored, switched off. */
  get visibleProjectTypes(): ProjectTypeConfig[] {
    return this.projectTypes.filter((pt) => isProjectTypeShown(pt.projectTypeId, this.allowedProjectTypeIds));
  }

  shouldDisableSelection(projectType: ProjectTypeConfig) {
    return this.visibleProjectTypes.filter((pt) => pt.active).length === 1 &&
      projectType.active;
  }

  formatYesNo(value: boolean) {
    return this.$t(`common.${value ? "yes" : "no"}`).toString();
  }

  formatProjectTypeName(projectTypeId: number): string {
    return formatProjectTypeName(this, projectTypeId);
  }

  getProjectTypeCode(projectTypeId: number): string {
    return getProjectTypeCode(this, projectTypeId, this.projectTypes) || '—';
  }

  getDefaultProjectTypeCode(projectTypeId: number): string {
    return this.$t(`projectDetails.projectTypeCodes.${projectTypeId}`).toString();
  }

  sanitizeBadge(val?: string | null): string {
    const s = (val ?? '').toString();
    if (s === '--') return s;
    return s.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 2);
  }

  formatScope(s: ExtendedProjectType) {
    return formatProjectScope(this, s);
  }

  /** Every field the template may contain. The editor decides which are still on offer. */
  get allTemplateFields(): ProjectNameTemplateField[] {
    return Object.values(ProjectNameTemplateField);
  }

  formatTemplateField(field: string): string {
    return this.$t(`settings.projectManagement.projectEditingSettings.templateFields.${field}`).toString();
  }

  /** Generates a preview string based on the active template fields. */
  get templatePreview(): string {
    const sampleValues: Record<ProjectNameTemplateField, string> = {
      [ProjectNameTemplateField.PROJECT_NUMBER]: "P-2026-001",
      [ProjectNameTemplateField.ZIP_CODE]: "12345",
      [ProjectNameTemplateField.CITY]: "Musterstadt",
      [ProjectNameTemplateField.STREET]: "Musterstraße",
      [ProjectNameTemplateField.HOUSE_NUMBER]: "42",
      [ProjectNameTemplateField.NAME]: "Mustermann",
      [ProjectNameTemplateField.COMMA]: ",",
    };

    return renderTemplateSample(this.activeTemplateFields, sampleValues);
  }

  initializeParametersProjectEditingSettings(
    projectEditingSettings: ProjectEditingSettings,
  ) {
    this.defaultDryingDurationInDays = projectEditingSettings.defaultDryingDurationInDays;
    this.defaultFollowUpInspectionIntervalInDays = projectEditingSettings.defaultFollowUpInspectionIntervalInDays;

    this.initialProjectEditingSettings = projectEditingSettings;
  }

  initializeParameters(
    projectEditingSettings?: ProjectEditingSettings,
  ) {
    if (projectEditingSettings) {
      this.initializeParametersProjectEditingSettings(projectEditingSettings);
    }

    const fromStore = this.projectManagementSettings;
    const loaded: ProjectTypeConfig[] | undefined = fromStore && fromStore.projectTypes;
    const base = JSON.parse(JSON.stringify(getDefaultProjectTypes())) as ProjectTypeConfig[];

    if (loaded && Array.isArray(loaded)) {
      const map = new Map<number, ProjectTypeConfig>(loaded.map(p => [p.projectTypeId, p]));
      this.projectTypes = base.map(p => {
        const saved = map.get(p.projectTypeId);
        return saved ? { ...p, ...saved } : p;
      });
    } else {
      this.projectTypes = base;
    }

    this.initialProjectTypes = JSON.parse(JSON.stringify(this.projectTypes));

    const savedTemplate = fromStore && fromStore.projectNameTemplate;
    if (savedTemplate && Array.isArray(savedTemplate) && savedTemplate.length > 0) {
      this.activeTemplateFields = [...savedTemplate];
    } else {
      this.activeTemplateFields = [...DEFAULT_PROJECT_NAME_TEMPLATE];
    }
    this.initialTemplateFields = [...this.activeTemplateFields];

    const displaySettings = fromStore && fromStore.displaySettings;
    this.projectOverviewTilesForOfficeProjects = displaySettings?.projectOverviewTilesForOfficeProjects ?? false;
    this.initialProjectOverviewTilesForOfficeProjects = this.projectOverviewTilesForOfficeProjects;

    const deviceAssignmentSettings = fromStore && fromStore.deviceAssignmentSettings;
    this.roomLevelDeviceAssignment = deviceAssignmentSettings?.roomLevelDeviceAssignment ?? false;
    this.initialRoomLevelDeviceAssignment = this.roomLevelDeviceAssignment;

    this.isEditMode = false;
  }

  async created() {
    this.isInitializing = true;

    const client = await loadClient();
    const dryingParameters = client?.dryingParameters;
    const allowedProjectTypeIds = client?.allowedProjectTypeIds;
    this.allowedProjectTypeIds = Array.isArray(allowedProjectTypeIds) ? allowedProjectTypeIds : null;
    const projectEditingSettings = this.projectManagementSettings.projectEditingSettings;

    if (projectEditingSettings && !projectEditingSettings.areDefaultProjectEditingSettings) {
      this.initializeParameters(projectEditingSettings);
    }
    else {
      this.initializeParameters({
        defaultDryingDurationInDays: dryingParameters?.defaultDryingDurationInDays ?? 14,
        defaultFollowUpInspectionIntervalInDays: dryingParameters?.defaultFollowUpInspectionIntervalInDays ?? 7,
      });
    }

    this.isInitializing = false;
  }

  async doSave() {
    // Validation: ensure at least one shown type is active (a withheld one is stored switched off)
    const atLeastOneActive = this.visibleProjectTypes.some(pt => pt.active);
    if (!atLeastOneActive) {
      SnackbarModule.showError({ key: "settings.projectManagement.projectSettings.projectTypesConfiguration.validation.atLeastOneActive" });
      throw new Error("validation");
    }

    // Validation: badge format and duplicates
    const definedBadges = this.visibleProjectTypes
      .map(pt => ({ id: pt.projectTypeId, badge: (pt.typeBadge ?? '').trim() }))
      .filter(x => x.badge.length > 0)
      .map(x => ({ id: x.id, badge: this.sanitizeBadge(x.badge) }));

    const invalidBadge = definedBadges.find(x => x.badge !== '--' && !/^[A-Za-z0-9]{1,2}$/.test(x.badge));
    if (invalidBadge) {
      SnackbarModule.showError({ key: "settings.projectManagement.projectSettings.projectTypesConfiguration.validation.typeBadgeInvalid" });
      throw new Error("validation");
    }

    // Validation: labels duplicates
    const definedLabels = this.visibleProjectTypes
      .map(pt => (pt.label ?? '').trim())
      .filter(s => s.length > 0)
      .map(s => s.toLowerCase());

    const labelSet = new Set<string>();
    for (const label of definedLabels) {
      if (labelSet.has(label)) {
        SnackbarModule.showError({ key: "settings.projectManagement.projectSettings.projectTypesConfiguration.validation.labelDuplicate" });
        throw new Error("validation");
      }
      labelSet.add(label);
    }

    // Normalize badges before save
    this.projectTypes = this.projectTypes.map(pt => (filterOutUndefined({
      ...pt,
      typeBadge: pt.typeBadge ? this.sanitizeBadge(pt.typeBadge) : pt.typeBadge,
    })));

    const projectEditingSettingsUpdate: ProjectEditingSettings = {
      defaultDryingDurationInDays: toNumber(this.defaultDryingDurationInDays),
      defaultFollowUpInspectionIntervalInDays: toNumber(this.defaultFollowUpInspectionIntervalInDays),

      areDefaultProjectEditingSettings: false,
    }

    const displaySettingsUpdate: DisplaySettings = {
      projectOverviewTilesForOfficeProjects: this.projectOverviewTilesForOfficeProjects,
    }

    const deviceAssignmentSettingsUpdate: DeviceAssignmentSettings = {
      roomLevelDeviceAssignment: this.roomLevelDeviceAssignment,
    }

    const update: ProjectManagementSettings = {
      ...this.projectManagementSettings,
      projectEditingSettings: projectEditingSettingsUpdate,
      displaySettings: displaySettingsUpdate,
      deviceAssignmentSettings: deviceAssignmentSettingsUpdate,
      // Every entry kept, a withheld type switched off: IRES Work falls back to its own default for a
      // missing entry (#2610081, docs/projects/project-types.md).
      projectTypes: projectTypesForSave(this.projectTypes, this.projectManagementSettings.projectTypes, this.allowedProjectTypeIds),
      projectNameTemplate: this.activeTemplateFields,
    };

    await updateProjectManagementSettings(update);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.initialProjectEditingSettings = update.projectEditingSettings!!;
    this.initialProjectTypes = JSON.parse(JSON.stringify(this.projectTypes));
    this.initialTemplateFields = [...this.activeTemplateFields];
    this.initialProjectOverviewTilesForOfficeProjects = this.projectOverviewTilesForOfficeProjects;
    this.initialRoomLevelDeviceAssignment = this.roomLevelDeviceAssignment;
  }

  mounted() {
    this.setToolbarTitle(this.$t("settings.projectManagement.projectEditingSettings.title").toString());
  }
}
